import { useEffect, useId, useMemo, useRef, useState } from "react";
import { useBuildingLocationData } from "./useBuildingLocationData";
import { nextFloor } from "./floorNaming";
import { observeElementResize, recentreTarget } from "./mapLifecycle";
import { useTranslation } from "react-i18next";
import { FillLayer, LineLayer } from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import Map, { Source, Layer, MapRef } from "react-map-gl";
import { ChevronRight, Loader2, Plus, Trash2 } from "lucide-react";
import type { ColumnDef } from "@tanstack/react-table";
import { userAPI } from "../../UserAPI";
import { Building, Floor, from, is } from "@the-real-insight/tri-model";

import { booleanPointInPolygon } from "@turf/boolean-point-in-polygon";
import { area, polygon } from "@turf/turf";
import InfoList from "../InfoList";
import { useAdministrationAPI } from "../../AdministrationAPI";
import { useNavigate } from "react-router-dom";
import { useModalContext } from "../../components/ModalProvider";
import DocumentUploadArea from "../../components/DocumentUploadArea";
import {
    ensureClosedRing,
    ringLonLatFromGeoSource,
} from "../../utilities/normalizeGeoShape";

import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { MAPBOX_TOKEN } from '../../utilities/mapbox';
import { entityHref } from "../../entities/entityNavigation";

class Properties {
  object: any;
}

const numberFormatArea = new Intl.NumberFormat("de-DE", {
  maximumFractionDigits: 0,
});

function getMapViewStateFromBuilding(building: any): {
  longitude: number;
  latitude: number;
  zoom: number;
} {
  const fallback = { longitude: 8.669347, latitude: 50.106277, zoom: 15 };
  if (!building) {
    return fallback;
  }
  const validBounds =
    Number.isFinite(building.minLon) &&
    Number.isFinite(building.maxLon) &&
    Number.isFinite(building.minLat) &&
    Number.isFinite(building.maxLat);
  if (validBounds) {
    return {
      longitude: 0.5 * (building.minLon + building.maxLon),
      latitude: 0.5 * (building.minLat + building.maxLat),
      zoom: 15,
    };
  }
  const ring = ringLonLatFromGeoSource(building);
  if (ring && ring.length) {
    const lons = ring.map((c) => c[0]);
    const lats = ring.map((c) => c[1]);
    return {
      longitude: (Math.min(...lons) + Math.max(...lons)) / 2,
      latitude: (Math.min(...lats) + Math.max(...lats)) / 2,
      zoom: 16,
    };
  }
  return fallback;
}

export default function BuildingLocationProperties(properties: Properties) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showSatelliteImage, setShowSatelliteImage] = useState(false);
  const { createFloor, deleteFloor, mapBuildingDwgZip } = useAdministrationAPI();
  const { pushError, showDeleteDialog, showConfirmationDialog } =
    useModalContext() as any;
  const {
    buildings,
    selectedBuilding,
    selectBuilding,
    floors,
    floorsLoading,
    reloadFloors,
  } = useBuildingLocationData(properties.object, {
    onError: (error) => pushError?.(error),
  });
  const loadFloors = () => reloadFloors(selectedBuilding?._id);
  const [zipBusy, setZipBusy] = useState(false);
  const [zipStatus, setZipStatus] = useState("");
  // Footprint <Source>/<Layer> must be added only after the base style has
  // loaded, otherwise mapbox silently drops the polygon. Readiness is recorded
  // per map instance (the key of the instance whose `load` fired) instead of a
  // boolean that is reset on remount: a stale or late event of another instance
  // can then never mark the current one ready, and nothing has to be reset.
  // The map is keyed by the object only, so loading data does not remount it.
  const mapKey = `loc-${String(properties.object?._id ?? properties.object?.id ?? "")}`;
  const mapRef = useRef<MapRef>(null);
  const [loadedMapKey, setLoadedMapKey] = useState<string | null>(null);
  const mapReady = loadedMapKey === mapKey;
  const satelliteToggleId = useId();

  const handleDwgZipFile = async (file: File | undefined | null) => {
    if (!file || !selectedBuilding?._id) {
      return;
    }
    if (zipBusy) {
      return;
    }
    const targetBuilding = selectedBuilding;
    const existingFloors = await userAPI.query(
      from(Floor.type).where(is("building").equal(`${targetBuilding._id}`)),
    );
    const floorCount = existingFloors?.length ?? 0;
    if (floorCount > 0) {
      showConfirmationDialog(
        t("buildingProperties.buildingLocationProperties.dwgOverwriteTitle"),
        t("buildingProperties.buildingLocationProperties.dwgOverwriteMessage", {
          count: floorCount,
        }),
        () => void runDwgZipImport(targetBuilding, file),
      );
      return;
    }
    void runDwgZipImport(targetBuilding, file);
  };

  const runDwgZipImport = async (targetBuilding: any, file: File) => {
    if (!file || !targetBuilding?._id) {
      return;
    }
    if (zipBusy) {
      return;
    }
    setZipBusy(true);
    setZipStatus(t("buildingProperties.buildingLocationProperties.dwgStarting"));
    try {
      const done = await mapBuildingDwgZip(
        new Building(targetBuilding),
        file,
        (event: any) => {
          if (event?.type === "progress" && event.message) {
            setZipStatus(event.message);
          }
        },
      );
      const count = done?.floors?.length ?? 0;
      setZipStatus(
        t("buildingProperties.buildingLocationProperties.dwgDone", { count }),
      );
      await loadFloors();
    } catch (error: any) {
      setZipStatus(
        `${t("buildingProperties.buildingLocationProperties.dwgFailed")}: ${
          error?.message ?? error
        }`,
      );
    } finally {
      setZipBusy(false);
    }
  };

  const floorColumns: ColumnDef<any, any>[] = [
    {
      id: "open",
      header: "",
      size: 50,
      enableSorting: false,
      cell: ({ row }) => (
        // Anzeigen ist ein Schritt ins Innere: Pfeil nach rechts, nie das Symbol für einen
        // externen Link (das nahmen Nutzer zum Schließen).
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label={t("entityPage.openFloor")}
          title={t("entityPage.openFloor")}
          disabled={!entityHref("Floor", row.original)}
          onClick={() => {
            const href = entityHref("Floor", row.original);
            if (href) navigate(href);
          }}
        >
          <ChevronRight aria-hidden="true" className="h-4 w-4" />
        </Button>
      ),
    },
    {
      id: "level",
      header: () => <div className="text-right">Ebene</div>,
      size: 80,
      enableSorting: false,
      cell: ({ row }) => <div className="text-right">{row.original.level}</div>,
    },
    {
      id: "name",
      header: () => <div className="text-right">Name</div>,
      size: 120,
      enableSorting: false,
      cell: ({ row }) => <div className="text-right">{row.original.name}</div>,
    },
    {
      id: "area",
      header: () => <div className="text-right">Fläche/㎡</div>,
      size: 100,
      enableSorting: false,
      cell: ({ row }) => <div className="text-right">{row.original.area}</div>,
    },
    {
      id: "delete",
      header: "",
      size: 50,
      enableSorting: false,
      cell: ({ row }) => (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label="Etage löschen"
          onClick={() => {
            showDeleteDialog("Etage löschen", row.original.name, async () => {
              try {
                await deleteFloor(row.original);
                await loadFloors();
              } catch (error) {
                pushError?.(error);
              }
            });
          }}
        >
          <Trash2 aria-hidden="true" className="h-4 w-4" />
        </Button>
      ),
    },
  ];

  const shapeFromBuilding = (building: any) => {
    const ring = ringLonLatFromGeoSource(building);
    if (!ring || ring.length < 3) {
      return { type: "FeatureCollection", features: [] };
    }
    const closed = ensureClosedRing(ring);
    return {
      type: "FeatureCollection",
      features: [
        {
          type: "Feature",
          properties: {},
          geometry: {
            type: "Polygon",
            coordinates: [closed],
          },
        },
      ],
    };
  };

  const buildingArea = (building: Building) => {
    const ring = ringLonLatFromGeoSource(building);
    if (!ring || ring.length < 3) {
      return 0;
    }
    return area(polygon([ensureClosedRing(ring)]));
  };

  const mapViewState = useMemo(() => {
    const primary =
      buildings.length > 0 ? buildings[0] : properties.object;
    return getMapViewStateFromBuilding(primary);
  }, [buildings, properties.object]);

  // react-map-gl ignores later `initialViewState` changes, so once the building
  // lookup resolves (or the map becomes ready after the data) the map is moved
  // explicitly. Only when the primary building differs from the one the map was
  // created with, so user panning is never overridden.
  const primaryBuildingId: string | undefined = buildings[0]?._id
    ? String(buildings[0]._id)
    : undefined;
  const centredOn = useRef<{ key: string; id: string | undefined } | null>(null);
  if (centredOn.current?.key !== mapKey) {
    centredOn.current = { key: mapKey, id: primaryBuildingId };
  }
  useEffect(() => {
    const target = recentreTarget(
      centredOn.current?.id,
      primaryBuildingId,
      mapViewState,
    );
    const map = mapRef.current?.getMap?.();
    if (!target || !map || !centredOn.current) {
      return;
    }
    map.jumpTo(target);
    centredOn.current.id = primaryBuildingId;
  }, [mapKey, mapReady, primaryBuildingId, mapViewState]);

  // mapbox-gl 1.x never notices container resizes (sidebar / assistant panel
  // animation, layout settling); without this the canvas keeps its first size
  // and the map stays grey until a refresh.
  const mapWrapperRef = useRef<HTMLDivElement>(null);
  useEffect(
    () =>
      observeElementResize(mapWrapperRef.current, () =>
        mapRef.current?.getMap?.()?.resize(),
      ),
    [],
  );

  return (
    <div className="flex min-w-0 max-w-[1400px] flex-col gap-4">
      <div className="flex items-center gap-2">
        <Switch
          id={satelliteToggleId}
          checked={showSatelliteImage}
          onCheckedChange={(checked) => setShowSatelliteImage(!!checked)}
        />
        <Label htmlFor={satelliteToggleId}>{t("entityPage.satelliteImage")}</Label>
      </div>
      {/* Karte und Detailspalte stehen erst ab lg nebeneinander; darunter
          untereinander, damit die Karte nicht die Seite aufdrückt. */}
      <div className="flex min-w-0 flex-col gap-4 lg:flex-row">
        <div
          ref={mapWrapperRef}
          className="h-[600px] w-full min-w-0 lg:w-[800px] lg:shrink-0"
        >
          <Map
            key={mapKey}
            ref={mapRef}
            mapboxAccessToken={MAPBOX_TOKEN}
            initialViewState={{
              longitude: mapViewState.longitude,
              latitude: mapViewState.latitude,
              zoom: mapViewState.zoom,
            }}
            style={{ width: "100%", height: "100%" }}
            mapStyle={
              showSatelliteImage
                ? "mapbox://styles/mapbox/satellite-streets-v9"
                : "mapbox://styles/mapbox/streets-v9"
            }
            onLoad={() => setLoadedMapKey(mapKey)}
            onClick={(event: any) => {
              const match = buildings.find((b: Building) => {
                const ring = ringLonLatFromGeoSource(b);
                if (!ring || ring.length < 3) {
                  return false;
                }
                return booleanPointInPolygon(
                  [event.lngLat.lng, event.lngLat.lat],
                  polygon([ensureClosedRing(ring)]),
                );
              });

              selectBuilding(match);
            }}
          >
            {mapReady &&
              buildings.map((b: Building) => {
              const ring = ringLonLatFromGeoSource(b);
              if (!ring || ring.length < 3) {
                return null;
              }
              const lineLayer: LineLayer = {
                id: `${b._id}-line`,
                type: "line",
                paint: {
                  "line-color": "#FFFFFF",
                  "line-width": 1,
                },
              };

              const fillLayer: FillLayer = {
                id: `${b._id}-fill`,
                type: "fill",
                paint: {
                  // Example of color interpolation using zoom
                  // more info: https://docs.mapbox.com/mapbox-gl-js/style-spec/expressions/
                  "fill-color": [
                    "interpolate",
                    ["linear"],
                    ["zoom"],
                    0,
                    "#eeddbb",
                    2,
                    "#0daa00",
                    3,
                    b === selectedBuilding
                      ? "rgba(187, 0, 0, 0.4)"
                      : "rgba(187, 187, 238, 0.6)",
                  ],
                },
              };
              return (
                <Source
                  key={`${b._id}-shape`}
                  id={`${b._id}-shape`}
                  type="geojson"
                  data={shapeFromBuilding(b) as any}
                >
                  <Layer {...lineLayer} />
                  <Layer {...fillLayer} />
                </Source>
              );
            })}
          </Map>
        </div>
        <div className="min-w-0 flex-1">
          {selectedBuilding?._id ? (
            <div className="flex min-w-0 flex-col gap-4">
              <h3 className="break-words text-lg font-bold leading-tight">
                {t("buildingProperties.buildingLocationProperties.building")}{" "}
                {selectedBuilding.id_osm} (
                {buildings.indexOf(
                  buildings.find(
                    (building: Building) =>
                      selectedBuilding._id === building._id,
                  ),
                ) + 1}
                /{buildings.length})
              </h3>
              <InfoList
                list={[
                  {
                    label: t(
                      "buildingProperties.buildingLocationProperties.area",
                    ),
                    value: `${numberFormatArea.format(buildingArea(selectedBuilding))} ㎡`,
                  },
                ]}
              ></InfoList>
              <div className="min-w-0">
                <DocumentUploadArea
                  onFiles={(files) => {
                    const file = files?.[0];
                    if (file) void handleDwgZipFile(file);
                  }}
                  accept={{
                    "application/zip": [".zip"],
                    "application/x-zip-compressed": [".zip"],
                  }}
                  hintKey="buildingProperties.buildingLocationProperties.dwgDragHint"
                  activeHintKey="buildingProperties.buildingLocationProperties.dwgDropHere"
                  multiple={false}
                  disabled={zipBusy}
                />
                {zipBusy || zipStatus ? (
                  <div className="mt-2 flex flex-wrap items-center gap-4">
                    {zipBusy ? <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" /> : <></>}
                    {zipStatus ? (
                      <span className="text-xs text-muted-foreground">
                        {zipStatus}
                      </span>
                    ) : (
                      <></>
                    )}
                  </div>
                ) : (
                  <></>
                )}
              </div>
              <div className="flex min-w-0 flex-col gap-2">
                <h4 className="text-sm font-bold leading-tight">Etagen</h4>
                <div>
                  <Button
                    data-guide-id="entity.building.createFloor"
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="gap-2"
                    // nextFloor() needs the settled floor list.
                    disabled={floorsLoading}
                    onClick={async () => {
                      if (!selectedBuilding?._id) {
                        return;
                      }
                      try {
                        // Name and level follow the building's own convention:
                        // `1.OG` by default, but `01. OG` or `OG1` where that is
                        // what the existing floors say. Plans, signage and keys
                        // use those names, and a portal spelling them its own way
                        // creates a mismatch someone reconciles by hand.
                        const { level, name } = nextFloor(floors);

                        await createFloor(
                          selectedBuilding,
                          new Floor({ name, level }),
                        );
                        await loadFloors();
                      } catch (error) {
                        pushError?.(error);
                      }
                    }}
                  >
                    <Plus aria-hidden="true" className="h-4 w-4" />
                    Etage anlegen
                  </Button>
                </div>
                <div className="overflow-x-auto">
                  <DataTable
                    columns={floorColumns}
                    data={floors}
                    loading={floorsLoading}
                    pageSize={20}
                    emptyMessage={t("settingsPanel.dataTable.noRecordsText")}
                  />
                </div>
              </div>
            </div>
          ) : (
            <></>
          )}
        </div>
      </div>
    </div>
  );
}
